import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  useTheme,
  Badge,
  Chip,
  ListItemAvatar,
  Paper,
  ListSubheader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  Category,
  ShoppingCart,
  People,
  Campaign,
  LocalOffer,
  PlayArrow,
  Settings,
  AccountCircle,
  Logout,
  CardGiftcard,
  Email,
  TrendingUp,
  Pending,
  RateReview,
  Notifications,
  Store,
  LocalShipping,
  Assignment,
  CheckCircle,
  Cancel,
  Visibility,
  Speed,
  NewReleases,
  ContactPhone,
  Refresh,
  FilterList,
  MoreVert,
  Star,
  Warning,
  Info,
  Delete,
  MarkEmailRead,
  Close,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import khizlyLogo from '../../assets/images/khizlyheader3.png';
import StorefrontIcon from '@mui/icons-material/Storefront';
import notificationService from '../../services/notificationService';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Products', icon: <Inventory />, path: '/products' },
  { text: 'Sellers', icon: <StorefrontIcon />, path: '/sellers' },
  { text: 'Pending Products', icon: <Pending />, path: '/pending-products' },
  { text: 'Categories', icon: <Category />, path: '/categories' },

  { text: 'Trending Products', icon: <TrendingUp />, path: '/products/trending' },
  { text: 'Orders', icon: <ShoppingCart />, path: '/orders' },
  { text: 'Users', icon: <People />, path: '/users' },
  { text: 'Customers', icon: <Email />, path: '/customers' },
  { text: 'Promotions', icon: <Campaign />, path: '/promotions' },
  { text: 'Promotion Requests', icon: <Pending />, path: '/promotion-requests' },
  { text: 'Hero Product Requests', icon: <Pending />, path: '/hero-product-requests' },
  { text: 'Sales', icon: <LocalOffer />, path: '/sales' },
  { text: 'Watch Videos', icon: <PlayArrow />, path: '/watch-videos' },
  { text: 'Gift Boxes', icon: <CardGiftcard />, path: '/gift-boxes' },
  { text: 'Reviews', icon: <RateReview />, path: '/reviews' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
];

const formatNotificationAction = (type, data, navigate) => {
  if (!data) return () => navigate('/dashboard');
  switch (type) {
    case 'order':
    case 'new_order':
    case 'ORDER_STATUS':
      return () => navigate(data?.orderId ? `/orders/${data.orderId}` : '/orders');
    case 'new_product':
    case 'product_status':
    case 'product_approval':
      return () => navigate(data?.productId ? '/pending-products' : '/pending-products');
    case 'category':
      return () => navigate('/categories');
    case 'new_seller':
    case 'seller_verification':
    case 'account_update':
      return () => navigate('/sellers');
    case 'promotion':
    case 'promotion_request':
      return () => navigate('/promotion-requests');
    default:
      return () => navigate('/dashboard');
  }
};

const getNotificationConfig = (type) => {
  const configs = {
    order: { icon: <ShoppingCart />, color: '#3b82f6' },
    ORDER_STATUS: { icon: <ShoppingCart />, color: '#3b82f6' },
    new_order: { icon: <ShoppingCart />, color: '#3b82f6' },
    new_product: { icon: <Inventory />, color: '#f59e0b' },
    product_status: { icon: <Inventory />, color: '#f59e0b' },
    product_approval: { icon: <Inventory />, color: '#f59e0b' },
    category: { icon: <Category />, color: '#8b5cf6' },
    new_seller: { icon: <Store />, color: '#10b981' },
    seller_verification: { icon: <Store />, color: '#ff2852' },
    account_update: { icon: <People />, color: '#0ea5e9' },
    promotion: { icon: <Campaign />, color: '#ec4899' },
    promotion_request: { icon: <Campaign />, color: '#ec4899' },
    message: { icon: <Email />, color: '#14b8a6' },
    system: { icon: <Info />, color: '#64748b' },
    review: { icon: <RateReview />, color: '#f97316' },
  };
  return configs[type] || { icon: <Info />, color: '#64748b' };
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [errorSnackbar, setErrorSnackbar] = useState({ open: false, message: '' });
  const [apiNotifications, setApiNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [activeNotifTab, setActiveNotifTab] = useState(0); // 0=All, 1=Unread, 2=Orders, 3=Products, 4=Categories
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useSelector((state) => state.auth);

  // Get data for fallback generated notifications
  const { users } = useSelector((state) => state.users);
  const { orders } = useSelector((state) => state.orders);
  const { products } = useSelector((state) => state.products);

  // Fetch real notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      setNotificationsLoading(true);
      const result = await notificationService.getNotifications(50);
      if (result?.success) {
        setApiNotifications(result.notifications || []);
        setUnreadCount(result.unreadCount || 0);
      }
    } catch (err) {
        console.error('Failed to fetch notifications:', err);
      } finally {
        setNotificationsLoading(false);
      }
  }, [user]);

  // Fetch notifications on mount and when user changes, and every 30s
  useEffect(() => {
    fetchNotifications();
    if (!user) return;
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, user]);

  // Mark notification as read when clicked
  const handleNotificationClick = useCallback(async (notification) => {
    try {
      if (notification?._id && !notification.isRead) {
        await notificationService.markAsRead(notification._id);
        setApiNotifications(prev =>
          prev.map(n => n._id === notification._id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  }, []);

  // Generate real + fallback notifications
  const notifications = useMemo(() => {
    const realNotifs = [];

    // 1. Real notifications from backend API - newest first (already sorted by backend)
    apiNotifications.forEach(notif => {
      const config = getNotificationConfig(notif.type);
      const createdAtDate = notif.createdAt ? new Date(notif.createdAt) : new Date();
      realNotifs.push({
        id: `api-${notif._id}`,
        _id: notif._id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        time: createdAtDate.toLocaleDateString() + ' ' + createdAtDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        createdAt: createdAtDate.getTime(),
        priority: notif.isRead ? 'low' : 'high',
        action: formatNotificationAction(notif.type, notif.data, navigate),
        onClick: () => handleNotificationClick(notif),
        icon: config.icon,
        color: config.color,
        isRead: notif.isRead,
        data: notif.data,
      });
    });

    // 2. Fallback generated notifications (shown only if no real matching notification exists for same event
    const existingTypes = new Set(apiNotifications.map(n => n.type));
    const existingProductNotifs = [];

    // Seller verification notifications
    const pendingSellers = users?.filter(user =>
      Array.isArray(user.role) ? user.role.includes('seller') : user.role === 'seller'
    ).filter(seller => seller.sellerStatus === 'pending') || [];

    pendingSellers.slice(0, 3).forEach(seller => {
      existingProductNotifs.push({
        id: `seller-verification-${seller._id}`,
        type: 'seller_verification',
        title: 'Seller Verification Request',
        message: `${seller.brandName || seller.name} wants to become a verified seller`,
        time: new Date(seller.createdAt).toLocaleDateString(),
        createdAt: new Date(seller.createdAt).getTime(),
        priority: 'high',
        action: () => navigate('/sellers'),
        icon: <Store />,
        color: '#ff2852',
        isRead: false,
      });
    });

    // New seller notifications
    const allSellers = users?.filter(user =>
      Array.isArray(user.role) ? user.role.includes('seller') : user.role === 'seller'
    ) || [];
    const sortedSellers = [...allSellers].sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );
    sortedSellers.slice(0, 3).forEach(seller => {
      existingProductNotifs.push({
        id: `new-seller-${seller._id}`,
        type: 'new_seller_fallback',
        title: 'New Seller Registered!',
        message: `${seller.brandName || seller.name} has registered with the ${seller.plan} plan.`,
        time: new Date(seller.createdAt).toLocaleDateString(),
        createdAt: new Date(seller.createdAt).getTime(),
        priority: 'high',
        action: () => navigate('/sellers'),
        icon: <Store />,
        color: '#10b981',
        isRead: false,
      });
    });

    // Pending products summary (only if no real new_product notifications are low count)
    const pendingProducts = products?.filter(product => product.verificationStatus === 'pending') || [];
    if (pendingProducts.length > 0 && !apiNotifications.some(n => n.type === 'new_product')) {
      existingProductNotifs.push({
        id: 'products-pending-summary',
        type: 'product_approval',
        title: 'Products Pending Approval',
        message: `${pendingProducts.length} products waiting for your approval`,
        time: 'Just now',
        createdAt: Date.now(),
        priority: 'medium',
        action: () => navigate('/pending-products'),
        icon: <Inventory />,
        color: '#f59e0b',
        isRead: false,
      });
    }

    // Recent order fallbacks
    const recentOrders = orders?.slice(0, 3) || [];
    recentOrders.forEach(order => {
      existingProductNotifs.push({
        id: `order-fallback-${order._id}`,
        type: 'new_order_fallback',
        title: 'New Order Received',
        message: `Order #${order._id?.slice(-8)} for PKR ${order.totalAmount?.toLocaleString()}`,
        time: new Date(order.createdAt).toLocaleDateString(),
        createdAt: new Date(order.createdAt).getTime(),
        priority: 'medium',
        action: () => navigate('/orders'),
        icon: <ShoppingCart />,
        color: '#3b82f6',
        isRead: false,
      });
    });

    // Combine real + fallbacks, then sort by createdAt DESC (newest top)
    const combined = [...realNotifs, ...existingProductNotifs];

    // Remove duplicates by id
    const seenIds = new Set();
    const uniqueNotifs = [];
    for (let i = combined.length - 1; i >= 0; i--) {
      const n = combined[i];
      if (!seenIds.has(n.id)) {
        seenIds.add(n.id);
        uniqueNotifs.unshift(n);
      }
    }

    // Sort by time (newest first)
    uniqueNotifs.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

    return uniqueNotifs;
  }, [apiNotifications, users, products, orders, navigate, handleNotificationClick]);

  // Get errors from different slices
  const promotionError = useSelector((state) => state.promotions?.error);
  const productError = useSelector((state) => state.products?.error);
  const categoryError = useSelector((state) => state.categories?.error);
  const orderError = useSelector((state) => state.orders?.error);

  // Filter notifications based on active tab (newest on top, already sorted by notifications useMemo)
  const filteredNotifications = useMemo(() => {
    const orderTypes = ['order', 'ORDER_STATUS', 'new_order', 'new_order_fallback'];
    const productTypes = ['new_product', 'product_status', 'product_approval'];
    const categoryTypes = ['category'];
    const sellerTypes = ['new_seller', 'seller_verification', 'account_update', 'new_seller_fallback'];

    return notifications.filter(n => {
      switch (activeNotifTab) {
        case 1: return !n.isRead;                         // Unread
        case 2: return orderTypes.includes(n.type);       // Orders
        case 3: return productTypes.includes(n.type);     // Products
        case 4: return categoryTypes.includes(n.type);    // Categories
        case 5: return sellerTypes.includes(n.type);      // Sellers
        case 0:
        default: return true;                             // All
      }
    });
  }, [notifications, activeNotifTab]);

  const unreadInTab = useMemo(() => filteredNotifications.filter(n => !n.isRead).length, [filteredNotifications]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    setActiveNotifTab(0);
    fetchNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setApiNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, []);

  const handleDeleteNotification = useCallback(async (apiNotificationId, stateId) => {
    try {
      if (apiNotificationId) {
        await notificationService.deleteNotification(apiNotificationId);
      }
      setApiNotifications(prev => prev.filter(n => n._id !== apiNotificationId));
      if (!apiNotificationId) {
        // For fallback-only notifications (api id undefined, just trigger re-fetch / rely on filtering above)
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
      setApiNotifications(prev => prev.filter(n => n._id !== apiNotificationId));
    }
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Show error snackbar when any error occurs
  React.useEffect(() => {
    const error = promotionError || productError || categoryError || orderError;
    if (error) {
      setErrorSnackbar({ open: true, message: error });
    }
  }, [promotionError, productError, categoryError, orderError]);

  const handleCloseErrorSnackbar = () => {
    setErrorSnackbar({ open: false, message: '' });
  };

  const drawer = (
    <Box sx={{
      height: '100%',
      backgroundColor: '#FFFFFF',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Toolbar sx={{
        minHeight: '80px !important',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#ff2852',
        borderBottom: '2px solid #ff2852',
      }}>
        <Box
          component="img"
          src={khizlyLogo}
          alt="Khizly Logo"
          sx={{
            maxWidth: '180px',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      </Toolbar>
      <Divider sx={{ borderColor: '#ff2852', opacity: 0.3 }} />
      <List sx={{
        flexGrow: 1,
        pt: 2,
        '& .MuiListItem-root': {
          px: 1.5,
        },
      }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                color: '#ff2852',
                '&:hover': {
                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  color: '#ff2852',
                  '& .MuiListItemIcon-root': {
                    color: '#ff2852',
                  },
                },
                '&.Mui-selected': {
                  backgroundColor: '#ff2852',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#ff2852',
                    color: '#FFFFFF',
                  },
                  '& .MuiListItemIcon-root': {
                    color: '#FFFFFF',
                  },
                },
              }}
            >
              <ListItemIcon sx={{
                color: location.pathname === item.path ? '#FFFFFF' : '#f40202ff',
                minWidth: 40,
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: location.pathname === item.path ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#ff2852',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderBottom: '2px solid #ff2852',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              noWrap 
              component="div"
              sx={{ 
                fontWeight: 600,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
            </Typography>
          </Box>

          {/* Notification Button */}
          <IconButton
            size="large"
            aria-label="notifications"
            onClick={handleNotificationMenuOpen}
            color="inherit"
            sx={{ 
              mr: 1,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <Notifications />
            </Badge>
          </IconButton>

          {/* User Profile Button */}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenuOpen}
            color="inherit"
            sx={{
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <Avatar sx={{ 
              width: { xs: 28, sm: 32 }, 
              height: { xs: 28, sm: 32 },
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}>
              {user?.name?.charAt(0)?.toUpperCase()}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User Profile Menu */}
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={() => {
            handleMenuClose();
            navigate('/settings');
          }}
          sx={{
            color: '#ff2852',
            '&:hover': {
              backgroundColor: 'rgba(255, 40, 82, 0.1)',
            }
          }}
        >
          <ListItemIcon>
            <AccountCircle fontSize="small" sx={{ color: '#ff2852' }} />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem 
          onClick={handleLogout}
          sx={{
            color: '#ff2852',
            '&:hover': {
              backgroundColor: 'rgba(255, 40, 82, 0.1)',
            }
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: '#ff2852' }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Notification Modal (Large Center Dialog) */}
      <Dialog
        id="notification-dialog"
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: { xs: '60vh', md: '70vh' },
            maxHeight: { xs: '90vh', md: '85vh' },
            bgcolor: '#fafafa',
            overflow: 'hidden',
          }
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            m: 0,
            p: 0,
            background: 'linear-gradient(135deg, #ff2852 0%, #e11d48 100%)',
            color: 'white',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Notifications />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Notifications
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {unreadCount} unread of total {notifications.length} notifications
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Refresh notifications">
                <IconButton
                  aria-label="refresh"
                  onClick={fetchNotifications}
                  disabled={notificationsLoading}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                >
                  {notificationsLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    <Refresh />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Mark all as read">
                <IconButton
                  aria-label="mark all as read"
                  onClick={handleMarkAllAsRead}
                  disabled={unreadCount === 0}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                >
                  <MarkEmailRead />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={handleNotificationMenuClose}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}
                >
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Filter Tabs */}
          <Tabs
            value={activeNotifTab}
            onChange={(_e, v) => setActiveNotifTab(v)}
            sx={{
              bgcolor: 'rgba(0,0,0,0.08)',
              '& .MuiTabs-indicator': {
                bgcolor: 'white',
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                color: 'rgba(255,255,255,0.85)',
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 600,
                minHeight: 46,
                py: 1,
                px: 2,
                '&.Mui-selected': { color: 'white' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`All (${notifications.length})`} />
            <Tab label={`Unread (${unreadCount})`} />
            <Tab label={`Orders (${notifications.filter(n => ['order', 'ORDER_STATUS', 'new_order', 'new_order_fallback'].includes(n.type)).length})`} />
            <Tab label={`Products (${notifications.filter(n => ['new_product', 'product_status', 'product_approval'].includes(n.type)).length})`} />
            <Tab label={`Categories (${notifications.filter(n => n.type === 'category').length})`} />
            <Tab label={`Sellers (${notifications.filter(n => ['new_seller', 'seller_verification', 'account_update', 'new_seller_fallback'].includes(n.type)).length})`} />
          </Tabs>
        </DialogTitle>

        {/* Content - Notification List */}
        <DialogContent dividers sx={{ p: 0, flex: 1, overflow: 'auto', bgcolor: '#f3f4f6' }}>
          {notificationsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
              <CircularProgress sx={{ color: '#ff2852' }} />
            </Box>
          ) : filteredNotifications.length > 0 ? (
            <List sx={{ p: 0 }}>
              {filteredNotifications.map((notification) => (
                <Paper
                  key={notification.id}
                  elevation={0}
                  onClick={async (e) => {
                    // Ignore clicks on action buttons
                    if (e.target.closest('[data-notif-action]')) return;
                    if (notification.onClick) await notification.onClick();
                    handleNotificationMenuClose();
                    if (notification.action) notification.action();
                  }}
                  sx={{
                    m: { xs: 1, sm: 1.5, md: 2 },
                    p: { xs: 1.5, sm: 2 },
                    border: notification.isRead ? '1px solid #e5e7eb' : '1px solid #fecdd3',
                    borderRadius: 2,
                    bgcolor: notification.isRead ? 'white' : 'rgba(255, 40, 82, 0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.07)',
                      bgcolor: notification.isRead ? '#fff' : 'rgba(255, 40, 82, 0.09)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {/* Icon */}
                    <Avatar
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: `${notification.color}15`,
                        color: notification.color,
                        flexShrink: 0,
                        mt: 0.3,
                        '& svg': { fontSize: 28 },
                      }}
                    >
                      {notification.icon}
                    </Avatar>

                    {/* Text */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                        <Typography variant="h6" fontWeight={notification.isRead ? 500 : 700} color="#111827" sx={{ fontSize: { xs: 15, sm: 16 } }}>
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <Tooltip title="Unread">
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: '#ff2852',
                                flexShrink: 0,
                                boxShadow: '0 0 0 3px rgba(255,40,82,0.15)',
                              }}
                            />
                          </Tooltip>
                        )}
                        <Chip
                          size="small"
                          label={notification.time}
                          sx={{
                            ml: 'auto',
                            fontSize: 11,
                            height: 22,
                            bgcolor: notification.isRead ? '#f3f4f6' : '#fecdd3',
                            color: notification.isRead ? '#4b5563' : '#be123c',
                            fontWeight: 500,
                          }}
                        />
                      </Box>

                      <Typography variant="body1" color="#374151" sx={{ fontSize: { xs: 13.5, sm: 14.5 }, lineHeight: 1.5, mb: 0.6 }}>
                        {notification.message}
                      </Typography>

                      {notification?.data?.sellerName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Store sx={{ fontSize: 15, color: '#10b981' }} />
                          <Typography variant="caption" sx={{ color: '#065f46', fontWeight: 600 }}>
                            by {notification.data.sellerName}
                          </Typography>
                        </Box>
                      )}

                      {/* Actions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8, flexWrap: 'wrap' }}>
                        {notification.action && (
                          <Button
                            size="small"
                            variant="outlined"
                            data-notif-action="view"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (notification.onClick) notification.onClick();
                              handleNotificationMenuClose();
                              notification.action();
                            }}
                            sx={{
                              borderColor: notification.color,
                              color: notification.color,
                              textTransform: 'none',
                              fontSize: 12.5,
                              px: 2,
                              fontWeight: 600,
                              '&:hover': {
                                bgcolor: `${notification.color}15`,
                                borderColor: notification.color,
                              },
                            }}
                          >
                            <Visibility sx={{ fontSize: 16, mr: 0.5 }} />
                            View Details
                          </Button>
                        )}
                        {!notification.isRead && (
                          <Button
                            size="small"
                            variant="text"
                            data-notif-action="mark-read"
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (notification.onClick) await notification.onClick();
                            }}
                            sx={{
                              color: '#6b7280',
                              textTransform: 'none',
                              fontSize: 12.5,
                              fontWeight: 500,
                            }}
                          >
                            <MarkEmailRead sx={{ fontSize: 15, mr: 0.5 }} />
                            Mark Read
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          data-notif-action="delete"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleDeleteNotification(notification._id, notification.id);
                          }}
                          sx={{
                            color: '#ef4444',
                            textTransform: 'none',
                            fontSize: 12.5,
                            fontWeight: 500,
                            ml: notification.action || !notification.isRead ? 0 : 'auto',
                            '&:hover': { bgcolor: '#fee2e2' },
                          }}
                        >
                          <Delete sx={{ fontSize: 15, mr: 0.5 }} />
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: { xs: 8, md: 14 },
                px: 3,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  width: 110,
                  height: 110,
                  borderRadius: '50%',
                  bgcolor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Notifications sx={{ fontSize: 56, color: '#9ca3af' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} color="#374151" sx={{ mb: 1 }}>
                No notifications in this view
              </Typography>
              <Typography variant="body2" color="#6b7280" sx={{ mb: 3 }}>
                {activeNotifTab === 1
                  ? 'All caught up! You have no unread notifications.'
                  : 'You will see new notifications here as they arrive.'}
              </Typography>
              {activeNotifTab !== 0 && (
                <Button
                  variant="contained"
                  onClick={() => setActiveNotifTab(0)}
                  sx={{
                    bgcolor: '#ff2852',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    '&:hover': { bgcolor: '#e11d48' },
                  }}
                >
                  View all notifications
                </Button>
              )}
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            p: 2,
            gap: 1,
            justifyContent: 'space-between',
            bgcolor: 'white',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <Box>
            <Typography variant="caption" color="#6b7280">
              Showing {filteredNotifications.length} notification{filteredNotifications.length === 1 ? '' : 's'}
              {unreadInTab > 0 && (
                <> · <Box component="span" sx={{ color: '#ff2852', fontWeight: 700 }}>{unreadInTab} unread</Box></>
              )}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="text"
              onClick={() => setActiveNotifTab(1)}
              disabled={unreadCount === 0}
              sx={{
                textTransform: 'none',
                color: '#ff2852',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(255,40,82,0.08)' },
              }}
            >
              Filter Unread
            </Button>
            <Button
              variant="contained"
              onClick={handleNotificationMenuClose}
              sx={{
                bgcolor: '#ff2852',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                '&:hover': { bgcolor: '#e11d48' },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#FFFFFF',
              borderRight: '2px solid #ff2852',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#FFFFFF',
              borderRight: '2px solid #ff2852',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          paddingTop: { xs: '70px', sm: '80px' },
          paddingBottom: { xs: '80px', sm: 3 },
          backgroundColor: '#f8f9fa',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={errorSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseErrorSnackbar}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Mobile Bottom Navigation */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        display: { xs: 'block', sm: 'none' },
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
      }}>
        <BottomNavigation
          showLabels
          value={location.pathname}
          onChange={(event, newValue) => {
            if (newValue === 'more') {
              handleDrawerToggle();
            } else {
              navigate(newValue);
            }
          }}
          sx={{
            bgcolor: '#ffffff',
            height: 65,
            borderTop: '1px solid #eee'
          }}
        >
          <BottomNavigationAction
            label="Home"
            value="/dashboard"
            icon={<Dashboard />}
            sx={{
              color: location.pathname === '/dashboard' ? '#ff2852' : 'text.secondary',
              '&.Mui-selected': { color: '#ff2852' }
            }}
          />
          <BottomNavigationAction
            label="Videos"
            value="/watch-videos"
            icon={<PlayArrow />}
            sx={{
              color: location.pathname === '/watch-videos' ? '#ff2852' : 'text.secondary',
              '&.Mui-selected': { color: '#ff2852' }
            }}
          />
          <BottomNavigationAction
            label="Orders"
            value="/orders"
            icon={<ShoppingCart />}
            sx={{
              color: location.pathname === '/orders' ? '#ff2852' : 'text.secondary',
              '&.Mui-selected': { color: '#ff2852' }
            }}
          />
          <BottomNavigationAction
            label="Categories"
            value="/categories"
            icon={<Category />}
            sx={{
              color: location.pathname === '/categories' ? '#ff2852' : 'text.secondary',
              '&.Mui-selected': { color: '#ff2852' }
            }}
          />
          <BottomNavigationAction
            label="More"
            value="more"
            icon={<MenuIcon />}
            sx={{ color: 'text.secondary' }}
          />
        </BottomNavigation>
      </Box>
    </Box>
  );
};

export default Layout;


